<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-40 flex items-start justify-center bg-black/48 px-3 py-6"
      @click="handleBackdropClick"
    >
      <div
        ref="panel"
        :data-testid="panelTestId"
        :class="[
          'border-conference-border mt-[5vh] flex max-h-[90vh] w-full flex-col overflow-hidden rounded-[1px] border bg-white text-slate-900 shadow-[0_2px_10px_rgba(0,0,0,0.2)] outline-none',
          widthClass,
        ]"
        :aria-label="title"
        aria-modal="true"
        role="dialog"
        tabindex="-1"
        @click.stop
      >
        <header
          v-if="!hideHeader && (title || description)"
          class="border-conference-line-soft bg-conference-surface-muted border-b px-2.5 py-1.5"
        >
          <h2 class="text-[11px] font-semibold text-slate-950">{{ title }}</h2>
          <p v-if="description" class="mt-0.5 text-[10px] leading-4 text-slate-600">
            {{ description }}
          </p>
        </header>

        <div :class="contentClass">
          <slot :close="close" />
        </div>

        <footer v-if="!hideFooter" class="border-conference-line-soft bg-conference-surface-muted border-t px-2.5 py-1">
          <slot name="footer" :close="close">
            <div class="flex justify-end">
              <CButton size="sm" tone="accent" @click="close">Close</CButton>
            </div>
          </slot>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import CButton from './CButton.vue';

const FOCUSABLE_SELECTOR =
  '[data-autofocus], button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const open = defineModel<boolean>('open', {
  default: false,
});

const props = withDefaults(
  defineProps<{
    closeOnBackdrop?: boolean;
    closeOnEscape?: boolean;
    contentPadding?: 'default' | 'none';
    contentScrollable?: boolean;
    description?: string;
    hideFooter?: boolean;
    hideHeader?: boolean;
    panelTestId?: string;
    title?: string;
    width?: 'lg' | 'md' | 'xl';
  }>(),
  {
    closeOnBackdrop: true,
    closeOnEscape: true,
    contentPadding: 'default',
    contentScrollable: true,
    description: undefined,
    hideFooter: false,
    hideHeader: false,
    panelTestId: undefined,
    width: 'lg',
  },
);

const panel = ref<HTMLElement | null>(null);
const lastFocusedElement = ref<HTMLElement | null>(null);

const widthClass = computed(() => {
  switch (props.width) {
    case 'md':
      return 'max-w-120';
    case 'xl':
      return 'max-w-176';
    default:
      return 'max-w-136';
  }
});

const contentClass = computed(() => {
  const classes = ['min-h-0', 'flex', 'flex-1', 'flex-col', 'bg-white', 'conference-text-control'];

  if (props.contentScrollable) {
    classes.push('overflow-y-auto');
  } else {
    classes.push('overflow-hidden');
  }

  if (props.contentPadding === 'none') {
    classes.push('p-0');
  } else {
    classes.push('px-2.5', 'py-2');
  }

  return classes;
});

function close(): void {
  open.value = false;
}

function handleBackdropClick(): void {
  if (!props.closeOnBackdrop) {
    return;
  }

  close();
}

function getFocusableElements(): HTMLElement[] {
  if (!panel.value) {
    return [];
  }

  return [...panel.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)].filter(
    (element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true',
  );
}

function focusFirstInteractiveElement(): void {
  const [focusTarget] = getFocusableElements();

  if (focusTarget) {
    focusTarget.focus();
    return;
  }

  panel.value?.focus();
}

function handleKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && props.closeOnEscape) {
    close();
    return;
  }

  if (event.key !== 'Tab' || !open.value) {
    return;
  }

  const focusableElements = getFocusableElements();

  if (focusableElements.length === 0) {
    event.preventDefault();
    panel.value?.focus();
    return;
  }

  const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const currentIndex = activeElement ? focusableElements.indexOf(activeElement) : -1;
  const lastIndex = focusableElements.length - 1;

  if (event.shiftKey) {
    if (currentIndex <= 0) {
      event.preventDefault();
      focusableElements[lastIndex]?.focus();
    }

    return;
  }

  if (currentIndex === -1 || currentIndex >= lastIndex) {
    event.preventDefault();
    focusableElements[0]?.focus();
  }
}

watch(open, async (value) => {
  if (value) {
    lastFocusedElement.value = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.addEventListener('keydown', handleKeydown);
    await nextTick();
    focusFirstInteractiveElement();
    return;
  }

  document.removeEventListener('keydown', handleKeydown);
  lastFocusedElement.value?.focus();
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown);
});
</script>
